/* eslint-disable react/prop-types */
'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, Day, DayPickerProps, DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';

import { cn, isSameCalendarDay } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

import { isWithinInterval, addDays, isAfter } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BookableDateStatus } from '@/lib/enums';
import type { BookableDate } from '@prisma/client';

import { formatCurrencyWithCode, formatCurrencyWithoutCode } from '@/lib/utils';
import useCurrency from '@/hooks/useCurrency';

export const formatePrice = (price_pn: number) => {
  const { currency } = useCurrency();
  const formattedPrice = formatCurrencyWithoutCode(price_pn, currency);
  if (formattedPrice >= 1000) {
    return `${(formattedPrice / 1000).toFixed(1)}K`;
  }
  return formattedPrice?.toString();
};

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  onClose: () => void;
  bookableDates?: BookableDate[];
  showAllDates?: boolean;
  className?: string;
  classNames?: string;
  selected?: DateRange;
  onSelect?: (_range: DateRange | undefined, _selectedDay?: Date) => void;
} & DayPickerProps;

function Calendar({ bookableDates, ...props }: CalendarProps) {
  const { currency } = useCurrency();
  const isDateAvailable = (date: Date) => {
    if (props.showAllDates) {
      return true;
    }

    const selected = props.selected as DateRange | undefined;
    const startingDate = selected?.from;
    if (!startingDate) return true;

    const startingDateInBookableDates = bookableDates?.find((d: BookableDate) =>
      isSameCalendarDay(new Date(d.date), startingDate)
    );

    const minStay = startingDateInBookableDates?.minStay || 1;

    if (minStay === 1) {
      return true;
    }

    // disabled dates are between checkin + 1 and checkin + minStay - 1
    const dateIsInMinStay = isWithinInterval(date, {
      start: addDays(startingDate, 1),
      end: addDays(startingDate, minStay - 1),
    });

    if (dateIsInMinStay) {
      return false;
    }

    return true;
  };
  const selectedDate = props.selected as DateRange | undefined;

  return (
    <DayPicker
      weekStartsOn={0}
      showOutsideDays={true}
      {...props}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        month_caption: 'flex items-center justify-center ',
        nav: 'absolute left-0 right-0 p-4 space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 bg-accent'
        ),
        button_previous: 'absolute top-1 left-4 p-4 cursor-pointer z-10',
        button_next: 'absolute top-1 right-4 p-4 cursor-pointer z-10',
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-9 md:w-12 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        range_start:
          'range-start rounded-l bg-secondary text-primary hover:bg-secondary/90 focus:bg-secondary',
        range_end:
          'range-end rounded-r bg-secondary text-primary hover:bg-secondary/90 focus:bg-secondary',
        selected: 'day-selected bg-secondary text-primary',
        disabled: 'text-muted-foreground line-through ',
        range_middle: 'aria-selected:bg-secondary aria-selected:text-white bg-secondary text-white',
        hidden: 'invisible',
      }}
      components={{
        Chevron: ({ ...props }) => {
          if (props.orientation === 'right') return <ChevronRight className="h-4 w-4" />;
          return <ChevronLeft className="h-4 w-4" />;
        },
        Day: (props) => {
          return <Day {...props}></Day>;
        },
        DayButton: (props) => {
          const { day, modifiers, ...restProps } = props;

          const isAvailable = isDateAvailable(day.date);

          const dateNumber = day.date.getDate();

          const bookableDate = bookableDates?.find((bookableDate: BookableDate) => {
            let date = bookableDate.date;
            if (typeof date === 'string') {
              date = new Date(date);
            }
            return isSameCalendarDay(date, day.date);
          });

          const hasBookableDates = bookableDates ? bookableDates?.length > 0 : false;

          const isSearch = true;

          const renderDay = () => {
            switch (true) {
              case bookableDate?.status === BookableDateStatus.ON_HOLD:
              case bookableDate?.status === BookableDateStatus.BOOKED:
                return (
                  <div className={`flex-col flex`}>
                    <span className="text-muted-foreground line-through opacity-50">
                      {dateNumber}
                    </span>
                    <div className="text-xs">
                      {bookableDate && formatePrice(bookableDate.price)}
                    </div>
                  </div>
                );
              case !isAvailable && hasBookableDates:
                return (
                  <div className={`flex-col flex`}>
                    <span className="opacity-40">{dateNumber}</span>
                    <div className="text-xs">
                      {bookableDate && formatePrice(bookableDate.price)}
                    </div>
                  </div>
                );
              case modifiers.disabled:
                return (
                  <div className={`flex-col flex`}>
                    <span className="text-muted-foreground line-through opacity-50">
                      {dateNumber}
                    </span>
                    <div className="text-xs">
                      {bookableDate && formatePrice(bookableDate.price)}
                    </div>
                  </div>
                );
              default:
                return (
                  <div className={`flex-col flex`}>
                    <span className="">{dateNumber}</span>
                    <div className="text-xs">
                      {bookableDate && formatePrice(bookableDate.price)}
                    </div>
                  </div>
                );
            }
          };
          return (
            <TooltipProvider delayDuration={80}>
              <Tooltip>
                <TooltipTrigger>
                  <button
                    {...restProps}
                    disabled={
                      !isSearch &&
                      (!isAvailable ||
                        bookableDate?.status === BookableDateStatus.BOOKED ||
                        bookableDate?.status === BookableDateStatus.ON_HOLD)
                    }
                    className={cn(
                      buttonVariants({ variant: 'ghost' }),
                      'h-9 w-9 md:h-12 md:w-12 text-center p-0 relative'
                    )}
                  >
                    {renderDay()}
                  </button>
                </TooltipTrigger>
                {isAvailable && bookableDate && isAfter(new Date(day.date), new Date()) && (
                  <TooltipContent className="p-3">
                    <div className="flex flex-col gap-1.5">
                      {/* Show price information */}
                      <div className="font-medium">
                        {bookableDate.price
                          ? `${formatCurrencyWithCode(bookableDate.price, currency)} / night`
                          : ''}
                      </div>

                      {/* Show day status based on selection context */}
                      {selectedDate?.from &&
                      isSameCalendarDay(new Date(selectedDate.from), new Date(day.date)) ? (
                        <div className="flex items-center text-brand-blue font-medium">
                          <span>Check-in day</span>
                        </div>
                      ) : null}

                      {selectedDate?.to &&
                      isSameCalendarDay(new Date(selectedDate.to), new Date(day.date)) ? (
                        <div className="flex items-center text-brand-pink font-medium">
                          <span>Check-out day</span>
                        </div>
                      ) : null}

                      {/* Show min stay information when relevant */}
                      {bookableDate.minStay && bookableDate.minStay > 1 && (
                        <div className="text-sm text-muted-foreground">
                          {/* If no dates selected or viewing dates before selection, show min stay info */}
                          {(!selectedDate?.from ||
                            !isSameCalendarDay(new Date(selectedDate.from), new Date(day.date))) &&
                            `${bookableDate.minStay}-night minimum stay`}

                          {/* If this is check-in date, show how many nights must be booked */}
                          {selectedDate?.from &&
                            isSameCalendarDay(new Date(selectedDate.from), new Date(day.date)) &&
                            `Must book ${bookableDate.minStay} nights`}
                        </div>
                      )}

                      {/* Guide user to select check-out date */}
                      {selectedDate?.from &&
                        !selectedDate.to &&
                        !isSameCalendarDay(new Date(selectedDate.from), new Date(day.date)) && (
                          <div className="text-sm font-medium text-brand-blue">
                            Select as check-out
                          </div>
                        )}

                      {/* Guide user to select check-in date */}
                      {!selectedDate?.from && !selectedDate?.to && (
                        <div className="text-sm font-medium text-brand-yellow">
                          Select as check-in
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        },
      }}
      footer={
        <div className="flex justify-end gap-2 mt-6 mb-2 mr-2">
          <Button
            variant={'outline'}
            onClick={() => {
              if (typeof props.onSelect != 'undefined')
                props.onSelect(
                  {
                    from: undefined,
                    to: undefined,
                  },
                  undefined
                );
            }}
          >
            Clear dates
          </Button>

          {selectedDate?.from && selectedDate?.to && (
            <Button
              onClick={() => {
                if (typeof props.onClose != 'undefined') props.onClose();
              }}
            >
              Confirm
            </Button>
          )}
        </div>
      }
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
